import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { travelerTripsService } from '../../services/supabaseService';
import { flightService } from '../../services/flightService';
import Header from '../../components/ui/Header';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import TripForm from './components/TripForm';
import TripCard from './components/TripCard';

const TravelerTripCreation = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTrip, setEditingTrip] = useState(null);

  const translations = {
    en: {
      title: 'My Trips',
      subtitle: 'Create and manage your travel availability',
      createTrip: 'Create New Trip',
      activeTrips: 'Active Trips',
      noTrips: 'No active trips',
      noTripsDesc: 'Create your first trip to start accepting delivery requests',
      loading: 'Loading trips...'
    },
    ko: {
      title: '내 여행',
      subtitle: '여행 가능 일정 생성 및 관리',
      createTrip: '새 여행 생성',
      activeTrips: '활성 여행',
      noTrips: '활성 여행 없음',
      noTripsDesc: '첫 여행을 생성하여 배송 요청 수락을 시작하세요',
      loading: '여행 로딩 중...'
    }
  };

  const t = translations?.[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication-screen');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadTrips();
    }
  }, [user]);

  const loadTrips = async () => {
    try {
      setLoading(true);
      const tripsData = await travelerTripsService?.getByTravelerId(user?.id);
      
      // Load flight information for each trip
      const tripsWithFlightInfo = await Promise.all(
        (tripsData || [])?.map(async (trip) => {
          const flightInfo = await flightService?.getByTripId(trip?.id);
          return { ...trip, flightInfo };
        })
      );
      
      setTrips(tripsWithFlightInfo || []);
    } catch (error) {
      console.error('Load trips error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrip = async (tripData) => {
    try {
      const { flightInfo, flightTicket, ...tripFields } = tripData;
      
      // Create trip
      const newTrip = {
        ...tripFields,
        travelerId: user?.id,
        status: 'active'
      };

      const created = await travelerTripsService?.create(newTrip);
      if (created) {
        // Upload flight ticket if provided
        let ticketFilePath = null;
        if (flightInfo?.flightTicket) {
          ticketFilePath = await flightService?.uploadTicket(
            flightInfo?.flightTicket,
            user?.id,
            created?.id
          );
        }
        
        // Create flight information
        const flightData = {
          tripId: created?.id,
          airline: flightInfo?.airline,
          flightNumber: flightInfo?.flightNumber,
          departureAirport: flightInfo?.departureAirport,
          departureTerminal: flightInfo?.departureTerminal || null,
          departureTime: flightInfo?.departureTime,
          arrivalAirport: flightInfo?.arrivalAirport,
          arrivalTerminal: flightInfo?.arrivalTerminal || null,
          arrivalTime: flightInfo?.arrivalTime,
          ticketFilePath: ticketFilePath,
          flightStatus: 'scheduled'
        };
        
        await flightService?.create(flightData);
        await loadTrips();
      }
    } catch (error) {
      console.error('Create trip error:', error);
    }
  };

  const handleUpdateTrip = async (tripId, tripData) => {
    try {
      const { flightInfo, flightTicket, ...tripFields } = tripData;
      
      // Update trip
      const updated = await travelerTripsService?.update(tripId, tripFields);
      if (updated) {
        // Get existing flight info
        const existingFlightInfo = await flightService?.getByTripId(tripId);
        
        // Upload new ticket if provided
        let ticketFilePath = existingFlightInfo?.ticketFilePath;
        if (flightInfo?.flightTicket) {
          // Delete old ticket if exists
          if (ticketFilePath) {
            await flightService?.deleteTicket(ticketFilePath);
          }
          ticketFilePath = await flightService?.uploadTicket(
            flightInfo?.flightTicket,
            user?.id,
            tripId
          );
        }
        
        // Update flight information
        const flightData = {
          airline: flightInfo?.airline,
          flightNumber: flightInfo?.flightNumber,
          departureAirport: flightInfo?.departureAirport,
          departureTerminal: flightInfo?.departureTerminal || null,
          departureTime: flightInfo?.departureTime,
          arrivalAirport: flightInfo?.arrivalAirport,
          arrivalTerminal: flightInfo?.arrivalTerminal || null,
          arrivalTime: flightInfo?.arrivalTime,
          ticketFilePath: ticketFilePath
        };
        
        if (existingFlightInfo) {
          await flightService?.update(existingFlightInfo?.id, flightData);
        } else {
          await flightService?.create({ ...flightData, tripId, flightStatus: 'scheduled' });
        }
        
        await loadTrips();
        setEditingTrip(null);
      }
    } catch (error) {
      console.error('Update trip error:', error);
    }
  };

  const handleDeleteTrip = async (tripId) => {
    try {
      // Get flight info to delete ticket
      const flightInfo = await flightService?.getByTripId(tripId);
      if (flightInfo?.ticketFilePath) {
        await flightService?.deleteTicket(flightInfo?.ticketFilePath);
      }
      
      // Delete trip (cascade deletes flight_information)
      const deleted = await travelerTripsService?.delete(tripId);
      if (deleted) {
        await loadTrips();
      }
    } catch (error) {
      console.error('Delete trip error:', error);
    }
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingTrip(null);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentLanguage={currentLanguage} onLanguageChange={setCurrentLanguage} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t?.loading}</p>
          </div>
        </div>
        <BottomTabNavigation currentLanguage={currentLanguage} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentLanguage={currentLanguage} onLanguageChange={setCurrentLanguage} />
      
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Plane" size={20} color="var(--color-primary)" />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
              {t?.title}
            </h1>
          </div>
          <p className="text-muted-foreground ml-13">{t?.subtitle}</p>
        </div>

        {/* Trip Creation Form */}
        <div className="mb-8">
          <TripForm
            onSubmit={editingTrip ? (data) => handleUpdateTrip(editingTrip?.id, data) : handleCreateTrip}
            currentLanguage={currentLanguage}
            editingTrip={editingTrip}
            onCancelEdit={handleCancelEdit}
          />
        </div>

        {/* Active Trips List */}
        <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="List" size={16} color="var(--color-success)" />
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              {t?.activeTrips}
            </h2>
            <span className="ml-auto text-sm text-muted-foreground">
              {trips?.length} {trips?.length === 1 ? 'trip' : 'trips'}
            </span>
          </div>

          {trips?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Plane" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t?.noTrips}</h3>
              <p className="text-muted-foreground">{t?.noTripsDesc}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trips?.map((trip) => (
                <TripCard
                  key={trip?.id}
                  trip={trip}
                  currentLanguage={currentLanguage}
                  onEdit={handleEditTrip}
                  onDelete={handleDeleteTrip}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <BottomTabNavigation currentLanguage={currentLanguage} />
    </div>
  );
};

export default TravelerTripCreation;